
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Button, Checkbox, Chip, Stack, Typography } from "@mui/material";
import BookingLifecycleOverview from '../../components/BookingLifecycleOverview';

export const BOOKING_TYPES = [
    { value: 'package', label: 'Wellness Package' },
    { value: 'consultation', label: 'Specialist Consultation' },
    { value: 'surgery', label: 'Surgery' },
    { value: 'second-opinion', label: 'Second Opinion' },
];

export const SERVICE_MODES = [
    { value: 'in-person', label: 'In-Person' },
    { value: 'pickup', label: 'Home Pickup' },
    { value: 'chat', label: 'Chat' },
    { value: 'call', label: 'Call' },
];

export const CONSULTATION_MODES = [
    { value: 'chat', label: 'Chat' },
    { value: 'call', label: 'Call' },
    { value: 'in-person', label: 'In-Person' },
];

export const BOOKING_STATUSES = [
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
];

export const PAYMENT_STATUSES = [
    { value: 'pending', label: 'Pending' },
    { value: 'paid', label: 'Paid' },
    { value: 'failed', label: 'Failed' },
    { value: 'refunded', label: 'Refunded' },
];

const formatDate = (value: string) => {
    if (!value) return '-';
    if (value.length === 8) {
        return `${value.substring(6, 8)}/${value.substring(4, 6)}/${value.substring(0, 4)}`;
    }
    return value;
}

const formatDateTime = (value?: string) => {
    if (!value) return '-';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const toLabelCase = (value?: string) => {
    if (!value) return '-';
    return value
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const getStatusColor = (value?: string) => {
    const normalized = value?.toLowerCase();
    if (normalized === 'confirmed' || normalized === 'completed' || normalized === 'report_added' || normalized === 'report_delivered' || normalized === 'paid') {
        return 'success' as const;
    }
    if (normalized === 'pending' || normalized === 'pending_payment' || normalized === 'in_progress' || normalized === 'rescheduled' || normalized === 'phlebo_assigned' || normalized === 'sample_collected' || normalized === 'patient_visited') {
        return 'warning' as const;
    }
    if (normalized === 'cancelled' || normalized === 'failed') {
        return 'error' as const;
    }
    if (normalized === 'refunded' || normalized === 'abandoned') {
        return 'info' as const;
    }

    return 'default' as const;
};

export const COLUMNS = [
    {
        header: 'Date',
        accessor: 'bookingDate',
        render: (item: any) => (
            <Typography variant="body2">{formatDate(item.bookingDate)}</Typography>
        )
    },
    {
        header: 'Time',
        accessor: 'bookingTime',
        render: (item: any) => (
            <Typography variant="body2">{item.bookingTime}</Typography>
        )
    },
    {
        header: 'Type',
        accessor: 'bookingType',
        render: (item: any) => {
            const type = BOOKING_TYPES.find(t => t.value === item.bookingType);
            return <Typography variant="body2">{type ? type.label : item.bookingType}</Typography>;
        }
    },
    {
        header: 'Mode',
        accessor: 'serviceMode',
        render: (item: any) => {
            const mode = SERVICE_MODES.find(m => m.value === item.serviceMode);
            return <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{mode ? mode.label : item.serviceMode}</Typography>;
        }
    },
    {
        header: 'Status',
        accessor: 'status',
        render: (item: any) => {
            const status = BOOKING_STATUSES.find(s => s.value === item.status);
            let color: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" = "default";
            if (item.status === 'confirmed' || item.status === 'completed') color = "success";
            if (item.status === 'pending') color = "warning";
            if (item.status === 'cancelled') color = "error";

            return <Chip label={status ? status.label : item.status} color={color} size="small" />;
        }
    },
    {
        header: 'Payment',
        accessor: 'paymentStatus',
        render: (item: any) => {
            const status = PAYMENT_STATUSES.find(s => s.value === item.paymentStatus);
            let color: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" = "default";
            if (item.paymentStatus === 'paid') color = "success";
            if (item.paymentStatus === 'pending') color = "warning";
            if (item.paymentStatus === 'failed') color = "error";
            if (item.paymentStatus === 'refunded') color = "info";

            return <Chip label={status ? status.label : item.paymentStatus} color={color} size="small" variant="outlined" />;
        }
    },
    {
        header: 'Price',
        accessor: 'price',
        render: (item: any) => (
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.price ? `₹${item.price}` : '-'}</Typography>
        )
    }
];

export const EXTERNAL_BOOKING_PROVIDER_TYPES = [
    { value: 'DOCTOR', label: 'Doctor' },
    { value: 'DIAGNOSTICS', label: 'Diagnostics' },
    { value: 'CAMP', label: 'Camp' },
];

export const EXTERNAL_BOOKING_INTEGRATIONS = [
    { value: 'labstack', label: 'Labstack' },
    { value: 'healthians', label: 'Healthians' },
    { value: 'betacura', label: 'BetaCura' },
];

export const EXTERNAL_APPOINTMENT_TYPES = [
    { value: 'ONLINE', label: 'Online' },
    { value: 'HOME_SAMPLE', label: 'Home Sample' },
    { value: 'IN_CLINIC', label: 'In Clinic' },
];

export const getExternalBookingColumns = (
    onView: (item: any) => void,
    onReport: (item: any) => void,
    options: {
        hideFinancials?: boolean;
        onToggleSkipReport?: (item: any, skipReport: boolean) => void;
        onToggleHealthCertificate?: (item: any, skipHealthCertificate: boolean) => void;
    } = {}
) => {
    const columns = [
    {
        header: 'Date',
        accessor: 'bookingDate',
        render: (item: any) => item.packageType === 'HYBRID' ? (
            <>
                <Typography variant="body2">Centre: {formatDate(item.legs?.centreVisit?.date || item.bookingDate)}</Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                    Home: {formatDate(item.legs?.homeCollection?.date)}
                </Typography>
            </>
        ) : <Typography variant="body2">{formatDate(item.bookingDate)}</Typography>
    },
    {
        header: 'Time',
        accessor: 'bookingTime',
        render: (item: any) => item.packageType === 'HYBRID' ? (
            <>
                <Typography variant="body2">Centre: {item.legs?.centreVisit?.time || item.bookingTime || '-'}</Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                    Home: {item.legs?.homeCollection?.time || '-'}
                </Typography>
            </>
        ) : <Typography variant="body2">{item.bookingTime || '-'}</Typography>
    },
    {
        header: 'Provider',
        accessor: 'providerType',
        render: (item: any) => (
            <>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{toLabelCase(item.providerType)}</Typography>
                <Typography variant="caption" color="text.secondary">
                    {item.externalProviderId ? `Provider ID: ${item.externalProviderId}` : '-'}
                </Typography>
            </>
        )
    },
    {
        header: 'Appointment',
        accessor: 'appointmentType',
        render: (item: any) => (
            <>
                <Typography variant="body2">{item.packageType === 'HYBRID' ? 'Hybrid · 2 visits' : toLabelCase(item.appointmentType)}</Typography>
                <Typography variant="caption" color="text.secondary">
                    {item.packageType === 'HYBRID' ? 'Centre + home collection' : item.externalSlotRef || item.bookingTime || '-'}
                </Typography>
            </>
        )
    },
    {
        header: 'Patient',
        accessor: 'patientName',
        render: (item: any) => (
            <>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.patientName || '-'}</Typography>
                <Typography variant="caption" color="text.secondary">{item.patientPhone || '-'}</Typography>
                <Typography variant="caption" display="block" color="text.secondary">{item.patientEmail || '-'}</Typography>
            </>
        )
    },
    {
        header: 'Booking Ref',
        accessor: 'externalBookingId',
        render: (item: any) => (
            <>
                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                    {item.packageType === 'HYBRID' && item.externalGroupId
                        ? `Group: ${item.externalGroupId}`
                        : item.externalBookingId || '-'}
                </Typography>
                {item.packageType === 'HYBRID' && (
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                        Centre: {item.externalBookingIds?.[0] || item.externalBookingId || '-'} · Home: {item.externalBookingIds?.[1] || '-'}
                    </Typography>
                )}
                <Typography variant="caption" color="text.secondary">{item._id || '-'}</Typography>
            </>
        )
    },
    {
        header: 'Corporate ID',
        accessor: 'corporateId',
        render: (item: any) => (
            <Typography variant="body2">{item.corporateId || '-'}</Typography>
        )
    },
    {
        header: 'Details',
        accessor: 'meta',
        render: (item: any) => (
            <>
                <Typography variant="body2">
                    {item.meta?.primaryConcern || item.meta?.labName || '-'}
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                    {item.meta?.consultationMode
                        ? `Mode: ${toLabelCase(item.meta.consultationMode)}`
                        : Array.isArray(item.meta?.packageIds) && item.meta.packageIds.length
                            ? `Packages: ${item.meta.packageIds.join(', ')}`
                            : '-'}
                </Typography>
            </>
        )
    },
    {
        header: 'Address',
        accessor: 'address',
        render: (item: any) => (
            <>
                <Typography variant="body2">{item.address || item.meta?.address || '-'}</Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                    {[item.meta?.city, item.meta?.state, item.meta?.pincode].filter(Boolean).join(', ') || '-'}
                </Typography>
            </>
        )
    },
    {
        header: 'Status & Lifecycle',
        accessor: 'status',
        render: (item: any) => {
            const displayStatus = item.packageType === 'HYBRID' ? item.overallStatus || item.status : item.status;
            return (
                <Stack spacing={0.25} sx={{ minWidth: 340 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Chip
                            label={toLabelCase(displayStatus)}
                            color={getStatusColor(displayStatus)}
                            size="small"
                        />
                    </Stack>
                    <BookingLifecycleOverview key={item._id} booking={item} />
                </Stack>
            );
        }
    },
    {
        header: 'Payment',
        hideForVendor: true,
        accessor: 'paymentStatus',
        render: (item: any) => (
            <Chip
                label={toLabelCase(item.paymentStatus)}
                color={getStatusColor(item.paymentStatus)}
                size="small"
                variant="outlined"
            />
        )
    },
    {
        header: 'Skip Report',
        accessor: 'skipReport',
        render: (item: any) => (
            <Checkbox
                size="small"
                checked={Boolean(item.skipReport)}
                onChange={(event) => options.onToggleSkipReport?.(item, event.target.checked)}
                disabled={!options.onToggleSkipReport}
            />
        )
    },
    {
        header: 'Enable Health Certificate',
        accessor: 'skipHealthCertificate',
        render: (item: any) => (
            <Checkbox
                size="small"
                checked={item.skipHealthCertificate === false}
                onChange={(event) => options.onToggleHealthCertificate?.(item, !event.target.checked)}
                disabled={!options.onToggleHealthCertificate}
            />
        )
    },
    {
        header: 'Reports',
        accessor: 'report',
        render: (item: any) => (
            <Button
                size="small"
                variant="outlined"
                disabled={Boolean(item.skipReport) || !(item.status === 'report_delivered' || (Array.isArray(item.report) && item.report.length))}
                onClick={() => onReport(item)}
            >
                Download
            </Button>
        )
    },
    {
        header: 'Price',
        hideForVendor: true,
        accessor: 'price',
        render: (item: any) => (
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.price ? `₹${item.price}` : '-'}</Typography>
        )
    },
    {
        header: 'Created',
        accessor: 'createdAt',
        render: (item: any) => (
            <Typography variant="body2">{formatDateTime(item.createdAt)}</Typography>
        )
    },
    {
        header: 'Updated',
        accessor: 'updatedAt',
        render: (item: any) => (
            <Typography variant="body2">{formatDateTime(item.updatedAt)}</Typography>
        )
    },
    {
        header: 'View',
        accessor: '_id',
        render: (item: any) => (
            <VisibilityOutlinedIcon
                color="primary"
                className="cursor-pointer"
                onClick={() => onView(item)}
            />
        )
    }
];

    return options.hideFinancials
        ? columns.filter((column: any) => !column.hideForVendor)
        : columns;
};
